import { Injectable, signal, computed, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CostMeterStatus } from './cost-meter.model';

const INITIAL_COST = 192.00;
const INITIAL_STATUS: CostMeterStatus = 'Running';
const STATUS_POOL: readonly CostMeterStatus[] = ['Running', 'Processing', 'Complete'];
const MIN_INCREMENT = 0.01;
const MAX_INCREMENT = 0.50;

/**
 * CostMeterService — manages the mocked AI cost and status meter state.
 *
 * This root-scoped service holds two private signals (cost and status) and exposes
 * them as readonly signals plus a computed formatted cost string. It subscribes to
 * Router.events to detect NavigationEnd and increment the cost/status on each navigation.
 *
 * State is in-memory only — a full browser refresh resets to initial values.
 */
@Injectable({ providedIn: 'root' })
export class CostMeterService {
  // Private mutable state signals
  private readonly _cost = signal<number>(INITIAL_COST);
  private readonly _status = signal<CostMeterStatus>(INITIAL_STATUS);

  // Public readonly derived signals
  /**
   * Current AI cost in USD (two decimal places).
   * Increments on each NavigationEnd event by a random amount in [0.01, 0.50].
   */
  readonly currentCost = this._cost.asReadonly();

  /**
   * Current status label — one of 'Running', 'Processing', or 'Complete'.
   * Randomly selected from the pool on each NavigationEnd event.
   */
  readonly currentStatus = this._status.asReadonly();

  /**
   * Formatted USD string derived from currentCost.
   * Always displays two decimal places (e.g. '$192.73').
   */
  readonly formattedCost = computed(() => `$${this._cost().toFixed(2)}`);

  constructor() {
    inject(Router).events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => this._increment());
  }

  /**
   * Increment the cost by a random amount and select a new status.
   * Called on each NavigationEnd event.
   *
   * @private
   */
  private _increment(): void {
    const delta = parseFloat(
      (Math.random() * (MAX_INCREMENT - MIN_INCREMENT) + MIN_INCREMENT).toFixed(2)
    );
    this._cost.update(c => parseFloat((c + delta).toFixed(2)));
    this._status.set(STATUS_POOL[Math.floor(Math.random() * STATUS_POOL.length)]);
  }
}
